<template>
  <transition name="cd-upload">
    <div class="cd-upload-overlay">
      <div class="cd-upload-card" role="dialog" aria-modal="true" aria-live="polite">

        <div class="cd-upload-head">
          <i class="cd-upload-icon"
             :class="finished ? 'fas fa-check-circle is-done' : 'fas fa-cloud-upload-alt'"></i>
          <span class="cd-upload-title">
            <slot name="title">{{ finished ? 'Subida completada' : 'Subiendo archivos' }}</slot>
          </span>
          <span v-if="showCounter" class="cd-upload-counter">{{ done }} de {{ total }}</span>
        </div>

        <p v-if="fileName && !finished" class="cd-upload-file">{{ fileName }}</p>

        <div class="cd-upload-bar">
          <div class="cd-upload-track"
               role="progressbar"
               :aria-valuenow="finished ? 100 : percent"
               aria-valuemin="0"
               aria-valuemax="100">
            <div class="cd-upload-fill"
                 :class="{ 'is-active' : !finished, 'is-indeterminate' : indeterminate }"
                 :style="indeterminate ? null : { width : (finished ? 100 : percent) + '%' }"></div>
          </div>
          <span class="cd-upload-percent">{{ finished ? 100 : percent }}%</span>
        </div>

      </div>
    </div>
  </transition>
</template>
<script>
export default {
  props : {
    progress  : { type : Number,  default : 0 },
    total     : { type : Number,  default : 0 },
    done      : { type : Number,  default : 0 },
    fileName  : { type : String,  default : '' },
    finished  : { type : Boolean, default : false }
  },
  computed : {
    // El progreso llega acumulado como 100/nº de archivos, así que trae
    // decimales (33.33333333333333). Se redondea aquí y no en el call site
    // para no tocar la lógica de subida de FileExplorer.
    percent () {
      return Math.min (100, Math.max (0, Math.round (this.progress)));
    },
    // Con un solo archivo el "1 de 1" sobra.
    showCounter () {
      return this.total > 1;
    },
    // Mientras no ha terminado ningún archivo, `progress` vale 0 y la barra se
    // quedaría vacía y quieta. En ese hueco se pasa a modo indeterminado: un
    // segmento que recorre la pista, sin fingir un porcentaje que no existe.
    indeterminate () {
      return !this.finished && this.percent === 0;
    }
  }
}
</script>
<style scoped>
/* Velo propio: este componente ya no reutiliza el .modal-mask de Modal.vue,
   del que dependía sin declararlo. El z-index queda por encima tanto de ese
   .modal-mask (500) como del overlay de vue-loading-overlay (9999), porque
   en la ruta del modal "Agregar Documentos" el diálogo de progreso convive
   con ambos. */
.cd-upload-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, .5);
}

.cd-upload-card {
  width: 420px;
  max-width: calc(100vw - 40px);
  padding: 20px 22px;
  background: #fff;
  border: 1px solid var(--cd-border-light);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, .18);
}

/* ---- Cabecera: icono + título + contador ---- */
.cd-upload-head {
  display: flex;
  align-items: center;
}

.cd-upload-icon {
  font-size: 18px;
  color: var(--cd-brand-green);
  margin-right: 10px;
}

.cd-upload-title {
  font-size: 15px;
  font-weight: 500;
  color: var(--cd-text-body);
}

.cd-upload-counter {
  margin-left: auto;
  padding-left: 10px;
  font-size: 13px;
  color: var(--cd-text-muted);
  white-space: nowrap;
}

/* ---- Nombre del archivo en curso ---- */
.cd-upload-file {
  margin: 10px 0 0;
  font-size: 13px;
  color: var(--cd-text-muted);
  /* Los nombres largos no deben ensanchar la tarjeta ni saltar de línea. */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ---- Barra ---- */
.cd-upload-bar {
  display: flex;
  align-items: center;
  margin-top: 14px;
}

.cd-upload-track {
  flex: 1;
  height: 8px;
  border-radius: 999px;
  background: var(--cd-border-light);
  overflow: hidden;
}

.cd-upload-fill {
  position: relative;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--cd-brand-green), var(--cd-brand-green-light));
  transition: width .3s ease;
}

/* El progreso avanza a saltos (100/nº de archivos), así que entre archivo y
   archivo la barra se queda quieta esperando al servidor. El brillo en
   movimiento indica que sigue trabajando. Se apaga al terminar. */
.cd-upload-fill.is-active:not(.is-indeterminate)::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg,
              rgba(255, 255, 255, 0) 0%,
              rgba(255, 255, 255, .45) 50%,
              rgba(255, 255, 255, 0) 100%);
  animation: cd-upload-shimmer 1.4s linear infinite;
}

@keyframes cd-upload-shimmer {
  from { transform: translateX(-100%); }
  to   { transform: translateX(100%); }
}

/* Modo indeterminado: hasta que termina el primer archivo no hay progreso real
   que pintar, así que en vez de una barra vacía y quieta recorre la pista un
   segmento. */
.cd-upload-fill.is-indeterminate {
  width: 35%;
  animation: cd-upload-indeterminate 1.2s ease-in-out infinite;
}

/* translateX en porcentaje se mide sobre el ancho del PROPIO elemento (35% de
   la pista), de ahí el 286%: 100 / 0.35 es lo que hace falta para que el
   segmento acabe de salir por el lado derecho. */
@keyframes cd-upload-indeterminate {
  from { transform: translateX(-100%); }
  to   { transform: translateX(286%); }
}

.cd-upload-percent {
  min-width: 44px;
  padding-left: 12px;
  text-align: right;
  font-size: 13px;
  font-weight: 500;
  color: var(--cd-brand-green);
  /* Cifras de ancho fijo: sin esto el porcentaje "baila" al pasar de 9 a 10. */
  font-variant-numeric: tabular-nums;
}

/* ---- Entrada / salida ---- */
.cd-upload-enter-active,
.cd-upload-leave-active {
  transition: opacity .2s ease;
}

.cd-upload-enter,
.cd-upload-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .cd-upload-fill.is-active::after {
    animation: none;
  }
  /* Queda un segmento fijo al 35%: sigue diciendo "hay algo en marcha", pero
     sin movimiento. */
  .cd-upload-fill.is-indeterminate {
    animation: none;
  }
  .cd-upload-fill {
    transition: none;
  }
}
</style>